import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { ReminderHistoryModal } from "@/components/reminder/ReminderHistoryModal.tsx";
import { ReminderSendModal } from "@/components/reminder/ReminderSendModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { TaskEditModal } from "@/pages/tasks/widgets/TaskEditModal.tsx";
import {
  useTask,
  useTaskActions,
  useTaskArchive,
  useTaskCommentDelete,
  useTaskCommentSave,
  useTaskCommentsSearchInfinite,
  useTaskComplete,
  useTaskStart,
  useTaskStop,
  useTaskUncomplete,
} from "@/queries/employees/use-employee-tasks.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ReminderEntityType } from "@/types/reminder.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployee, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiTask,
  ApiTaskAction,
  ApiTaskStatus,
  getTaskAction,
  getTaskStatus,
  getTaskWorkflowAction,
} from "@/types/task.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import {
  Avatar,
  Box,
  Button,
  ConfirmationModal,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  HtmlViewer,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  SplitButton,
  Stack,
  TabItem,
  Tabs,
  Text,
  Title,
  useModals,
  useToasts,
  parseUrlHelper,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { ApiWorkflowShort } from "@/types/workflows/workflow.types.ts";
import { ApiEntityTypes } from "@/types/common.types.ts";
import { CommentForm } from "@/components/comments/CommentForm.tsx";
import { CommentInline } from "@/components/comments/CommentInline.tsx";

function TaskEmployeeItem({ title, employee }: { title: string; employee: ApiEmployee }) {
  const { renderEmployeeName } = useFormatter();

  return (
    <ListItem
      key={employee.id}
      avatarUrl={getEmployeeAvatarUrl(employee, "sm")}
      forceShowAvatar={true}
      preTitle={title}
      title={renderEmployeeName(employee)}
      captionSlot={<Text className={"text-secondary"}>{getEmployeePositionTitle(employee)}</Text>}
      to={`/employees/${employee.id}`}
      blank={true}
    />
  );
}

function TaskWorkflowItem({ title, workflow }: { title: string; workflow: ApiWorkflowShort }) {
  const { t } = useTranslation();

  return (
    <ListItem
      key={workflow.id}
      icon={<Avatar icon={<FontAwesomeIcon icon="fa-light fa-user-circle" />} />}
      preTitle={title}
      title={workflow.title}
      captionSlot={<Text className={"text-secondary"}>{t("tasks:task_workflow_caption")}</Text>}
    />
  );
}

function TaskActivityItem({ action }: { action: ApiTaskAction }) {
  const { renderTimeAgoShort, renderEmployeeName } = useFormatter();
  const { t } = useTranslation();

  let actionText: string;
  if (action.created_by_workflow) {
    actionText = action.assign_to_employee
      ? `${getTaskWorkflowAction(action.action_type)} ${t("tasks:task_action_and_assigned_to", { name: renderEmployeeName(action.assign_to_employee) })}`
      : getTaskWorkflowAction(action.action_type);
  } else {
    actionText = action.assign_to_employee
      ? `${getTaskAction(action.action_type)} ${t("tasks:task_action_and_assigned_to", { name: renderEmployeeName(action.assign_to_employee) })}`
      : getTaskAction(action.action_type);
  }

  let avatar = <Avatar icon={<FontAwesomeIcon icon="fa-light fa-user-circle" />} size={"sm"} />;
  let title: string = "";
  if (action.created_by) {
    avatar = <EmployeeAvatar size={"sm"} employee={action.created_by} />;
    title = renderEmployeeName(action.created_by);
  } else if (action.created_by_workflow) {
    title = action.created_by_workflow.title;
  }

  return (
    <Stack className={"w-full overflow-hidden"} gap={"sm"}>
      <Stack gap={"sm"} direction={"horizontal"} items={"center"} className={"overflow-hidden"}>
        {avatar}
        <DotSeparator className={"text-secondary text-nowrap text-ellipsis overflow-hidden"}>
          {title && <Text>{title.trim()}</Text>}
          <Text>{renderTimeAgoShort(action.datetime_created_at)}</Text>
        </DotSeparator>
      </Stack>
      <Text>{actionText}</Text>
    </Stack>
  );
}

interface TaskViewModalProps extends ModalProps {
  task: ApiTask;
}

export function TaskViewModal({ task, ...props }: TaskViewModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { renderDate, renderTimeAgo } = useFormatter();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openModal, close } = useModals();
  const { showInfo } = useToasts();

  const { data: liveTask } = useTask(task.id);
  const currentTask = liveTask ?? task;
  const { data: employeeMe } = useEmployeeMe();

  const isNotMyTask = currentTask.assign_to_employee.id !== employeeMe?.id;

  const taskArchive = useTaskArchive();
  const taskComplete = useTaskComplete();
  const taskUncomplete = useTaskUncomplete();
  const taskStart = useTaskStart();
  const taskStop = useTaskStop();

  const { data: actions, isLoading: isActionsLoading } = useTaskActions(currentTask.id);

  const {
    data: commentsPages,
    hasNextPage,
    isLoading: isCommentsLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useTaskCommentsSearchInfinite(currentTask.id, {});
  const comments = useMemo(
    () => commentsPages?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [commentsPages]
  );

  const commentSave = useTaskCommentSave();
  const commentDelete = useTaskCommentDelete();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.tasks.taskViewed, {
      [analyticProperties.id]: task.id,
    });

    const taskIdFromURL = parseUrlHelper.getNumber(searchParams, "task_id");

    if (!taskIdFromURL) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("task_id", `${currentTask.id}`);
      navigate({ search: newParams.toString() }, { replace: true });
    }

    return () => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("task_id");
      navigate({ search: newParams.toString() }, { replace: true });
    };
  });

  const handleEdit = () => {
    close(id);
    openModal(TaskEditModal, {
      task: currentTask,
      onClose: () => openModal(TaskViewModal, { task: currentTask }),
    });
  };

  const handleCopyLink = () => {
    const url = new URL("/tasks", window.location.origin);
    url.searchParams.set("task_id", `${currentTask.id}`);
    copyTextWithNotify(url.toString()).then((copied) => {
      if (!copied) return;
      showInfo({ title: t("common:link_copied") });
    });
  };

  const handleComplete = async () => {
    await taskComplete.mutateAsync(currentTask.id, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.tasks.completed, {
          [analyticProperties.id]: currentTask.id,
        });
      },
    });
  };

  const handleUncomplete = async () => {
    await taskUncomplete.mutateAsync(currentTask.id, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.tasks.uncompleted, {
          [analyticProperties.id]: currentTask.id,
        });
      },
    });
  };

  const handleStart = async () => {
    await taskStart.mutateAsync(currentTask.id, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.tasks.started, {
          [analyticProperties.id]: currentTask.id,
        });
      },
    });
  };

  const handleStop = async () => {
    await taskStop.mutateAsync(currentTask.id, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.tasks.stopped, {
          [analyticProperties.id]: currentTask.id,
        });
      },
    });
  };

  const handleArchive = () => {
    openModal(ConfirmationModal, {
      question: t("tasks:archive_task"),
      text: t("tasks:archive_task_confirm"),
      acceptTitle: t("common:archive"),
      onAccept: async () => {
        await taskArchive.mutateAsync(currentTask.id, {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.tasks.archived, {
              [analyticProperties.id]: currentTask.id,
            });
          },
        });
        close(id);
      },
    });
  };

  const handleCommentSave = (comment?: ApiComment) => async (text: string, fileUuids: string[]) => {
    const result = await commentSave.mutateAsync({
      taskId: currentTask.id,
      commentId: comment?.id,
      payload: { text, file_uuids: fileUuids },
    });
    return !!result.comment;
  };

  const handleCommentDelete = (comment: ApiComment) => async () => {
    await commentDelete.mutateAsync({ taskId: currentTask.id, commentId: comment.id });
  };

  return (
    <Modal
      {...props}
      title={t("tasks:task")}
      size={"lg"}
      withCloser={true}
      closeByOutsidePress
      actions={
        currentTask.status === ApiTaskStatus.completed || currentTask.is_archived ? (
          <Button size={"lg"} className={"w-full"} onClick={() => close(id)}>
            {t("common:ok")}
          </Button>
        ) : undefined
      }
      headerActionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("common:actions")}
              </Button>
            }
          >
            {currentTask.acl.can_edit && (
              <DropdownMenuItem
                leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                onClick={handleEdit}
                title={t("common:edit")}
              />
            )}
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon="fa-light fa-link" />}
              onClick={handleCopyLink}
              title={t("common:copy_link")}
            />
            {isNotMyTask && currentTask.status !== ApiTaskStatus.completed && (
              <>
                <DropdownMenuItem
                  onClick={() =>
                    openModal(ReminderSendModal, {
                      recipients: [currentTask.assign_to_employee],
                      entityType: ReminderEntityType.task,
                      entityIds: [currentTask.id],
                    })
                  }
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-bell"} />}
                  title={t("core:remind")}
                  description={
                    currentTask.datetime_last_reminder
                      ? t("core:last_reminded", {
                          time: renderTimeAgo(currentTask.datetime_last_reminder),
                        })
                      : ""
                  }
                />
                <DropdownMenuItem
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-clock-rotate-left" />}
                  onClick={() =>
                    openModal(ReminderHistoryModal, {
                      filter: {
                        entity_type: ReminderEntityType.task,
                        entity_ids: [currentTask.id],
                      },
                    })
                  }
                  title={t("core:reminder_history")}
                />
              </>
            )}

            {currentTask.acl.can_delete && (
              <DropdownMenuItem
                leftIcon={<FontAwesomeIcon icon="fa-light fa-box-archive" />}
                onClick={handleArchive}
                title={t("common:archive") + "..."}
              />
            )}
          </DropdownMenu>
          {currentTask.acl.can_edit &&
            (currentTask.status === ApiTaskStatus.completed ? (
              <Button
                variant={"secondary"}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-rotate-left" />}
                onClick={handleUncomplete}
                isLoading={taskUncomplete.isPending}
              >
                {t("tasks:mark_todo")}
              </Button>
            ) : (
              <SplitButton
                variant={"primary"}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-check" />}
                onClick={handleComplete}
                isLoading={taskComplete.isPending}
                disabled={taskStart.isPending || taskStop.isPending}
                menuLabel={t("tasks:change_status")}
                menu={
                  currentTask.status === ApiTaskStatus.in_progress ? (
                    <DropdownMenuItem
                      title={t("tasks:mark_todo")}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-rotate-left" />}
                      onClick={handleStop}
                    />
                  ) : (
                    <DropdownMenuItem
                      title={t("tasks:mark_in_progress")}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-play" />}
                      onClick={handleStart}
                    />
                  )
                }
              >
                {t("tasks:mark_complete")}
              </SplitButton>
            ))}
        </Stack>
      }
    >
      <Stack>
        <ListItem
          emphasis={"middle"}
          preTitle={t("common:title")}
          titleSlot={<Title size={"lg"} header={currentTask.title} />}
        />
        <ListItem
          emphasis={"middle"}
          preTitle={t("common:status")}
          title={getTaskStatus(currentTask.status)}
          valueSlot={
            currentTask.date_due && (
              <Stack items={"end"}>
                <Text>{t("common:due_date")}</Text>
                <Text className={"text-secondary"}>{renderDate(currentTask.date_due)}</Text>
              </Stack>
            )
          }
        />
        {currentTask.description && (
          <ListItem
            emphasis={"middle"}
            preTitle={t("common:description")}
            titleSlot={<HtmlViewer content={currentTask.description} />}
          />
        )}

        {currentTask.files && currentTask.files.length > 0 && (
          <Box className={"mt-2"}>
            <Stack gap={"sm"} items={"start"}>
              {currentTask.files.map((file) => (
                <FileItem key={`file${file.id}`} file={file} />
              ))}
            </Stack>
          </Box>
        )}

        <Box>
          <Title header={t("common:persons")} size={"xs"} paddingTop={true} />
          {currentTask.created_by && (
            <TaskEmployeeItem title={t("common:created_by")} employee={currentTask.created_by} />
          )}
          {currentTask.created_by_workflow && (
            <TaskWorkflowItem
              title={t("common:created_by")}
              workflow={currentTask.created_by_workflow}
            />
          )}
          <TaskEmployeeItem
            title={t("common:assign_to")}
            employee={currentTask.assign_to_employee}
          />
          {currentTask.related_employee && (
            <TaskEmployeeItem
              title={t("tasks:related_employee")}
              employee={currentTask.related_employee}
            />
          )}
          {currentTask.visible_to_employees.map((employee) => (
            <TaskEmployeeItem key={employee.id} title={t("tasks:watcher")} employee={employee} />
          ))}
        </Box>

        <Box>
          <Tabs>
            <TabItem title={t("common:comments")}>
              <Stack gap={"lg"} className={"mt-5"}>
                <CommentForm
                  entityId={task.id}
                  entityType={ApiEntityTypes.task}
                  onSave={handleCommentSave()}
                  useMentions={false}
                />
                {isCommentsLoading ? (
                  <>
                    <Skeleton className={"h-15 w-auto"} />
                    <Skeleton className={"h-15 w-auto"} />
                    <Skeleton className={"h-15 w-auto"} />
                  </>
                ) : (
                  <>
                    {comments.map((comment) => (
                      <CommentInline
                        key={comment.id}
                        comment={comment}
                        onEdit={handleCommentSave(comment)}
                        onDelete={handleCommentDelete(comment)}
                        useMentions={false}
                      />
                    ))}
                    {hasNextPage && (
                      <Stack direction={"horizontal"}>
                        <Button
                          size={"sm"}
                          isLoading={isFetchingNextPage}
                          onClick={async () => {
                            await fetchNextPage();
                          }}
                        >
                          {t("common:show_more")}
                        </Button>
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </TabItem>
            <TabItem title={t("common:activity")}>
              <Stack gap={"lg"} className={"mt-5"}>
                {isActionsLoading ? (
                  <>
                    <Skeleton className={"h-15 w-auto"} />
                    <Skeleton className={"h-15 w-auto"} />
                    <Skeleton className={"h-15 w-auto"} />
                  </>
                ) : (
                  actions?.map((action) => (
                    <TaskActivityItem
                      key={`${action.action_type}-${action.datetime_created_at.getTime()}`}
                      action={action}
                    />
                  ))
                )}
              </Stack>
            </TabItem>
          </Tabs>
        </Box>
      </Stack>
    </Modal>
  );
}
